import { useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, X } from 'lucide-react';
import { useI18n } from '@/i18n/use-i18n';
import { AssetClassCode } from '@/types/assets';
import './add-asset-dialog.css';

interface AssetClassOption {
  id: string;
  code: AssetClassCode;
  name: string;
}

interface AddAssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classes: AssetClassOption[];
  onSubmit: (data: {
    assetClassId: string;
    ticker: string;
    quantity: number;
    rating: number;
    averagePrice: number;
  }) => void;
  isSubmitting?: boolean;
  initialTicker?: string;
}

export function AddAssetDialog({
  open,
  onOpenChange,
  classes,
  onSubmit,
  isSubmitting = false,
  initialTicker = '',
}: AddAssetDialogProps) {
  const { strings } = useI18n();
  const t = strings.targetPortfolio.addAsset;

  const [assetClassId, setAssetClassId] = useState('');
  const [ticker, setTicker] = useState(initialTicker);
  const [quantity, setQuantity] = useState<number>(0);
  const [rating, setRating] = useState<number>(7);
  const [averagePrice, setAveragePrice] = useState<number>(0);

  const resetForm = () => {
    setAssetClassId('');
    setTicker(initialTicker);
    setQuantity(0);
    setRating(7);
    setAveragePrice(0);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!assetClassId || !ticker.trim()) return;
    if (quantity > 0 && averagePrice <= 0) return;

    onSubmit({
      assetClassId,
      ticker: ticker.toUpperCase().trim(),
      quantity,
      rating: Math.min(10, Math.max(0, Math.round(rating))),
      averagePrice,
    });
  };

  const isValid = assetClassId && ticker.trim() && (quantity === 0 || averagePrice > 0);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="add-asset-dialog__overlay" />
        <Dialog.Content className="add-asset-dialog">
          <div className="add-asset-dialog__header">
            <div>
              <Dialog.Title className="add-asset-dialog__title">
                {t.dialogTitle}
              </Dialog.Title>
              <Dialog.Description className="add-asset-dialog__description">
                {t.dialogDescription}
              </Dialog.Description>
            </div>
            <Dialog.Close className="add-asset-dialog__close" aria-label="Fechar">
              <X size={18} />
            </Dialog.Close>
          </div>

          <form className="add-asset-dialog__form" onSubmit={handleSubmit}>
            <div className="add-asset-dialog__field">
              <label htmlFor="add-asset-class">{t.classLabel}</label>
              <select
                id="add-asset-class"
                value={assetClassId}
                onChange={(e) => setAssetClassId(e.target.value)}
                required
              >
                <option value="">{t.classPlaceholder}</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="add-asset-dialog__row">
              <div className="add-asset-dialog__field">
                <label htmlFor="add-asset-ticker">{t.tickerLabel}</label>
                <input
                  id="add-asset-ticker"
                  type="text"
                  placeholder={t.tickerPlaceholder}
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                  required
                />
              </div>
              <div className="add-asset-dialog__field">
                <label htmlFor="add-asset-quantity">{t.quantityLabel}</label>
                <input
                  id="add-asset-quantity"
                  type="number"
                  min={0}
                  step={1}
                  value={quantity || ''}
                  onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                />
              </div>
            </div>

            <div className="add-asset-dialog__row">
              <div className="add-asset-dialog__field">
                <label htmlFor="add-asset-avg-price">{t.averagePriceLabel}</label>
                <input
                  id="add-asset-avg-price"
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder={t.averagePricePlaceholder}
                  value={averagePrice || ''}
                  onChange={(e) => setAveragePrice(Math.max(0, Number(e.target.value)))}
                />
              </div>
              <div className="add-asset-dialog__field">
                <label htmlFor="add-asset-rating">
                  {t.ratingLabel}
                </label>
                <input
                  id="add-asset-rating"
                  type="number"
                  min={0}
                  max={10}
                  step={1}
                  value={rating}
                  onChange={(e) => setRating(Math.min(10, Math.max(0, Number(e.target.value))))}
                />
                <span className="add-asset-dialog__hint">{t.ratingHint}</span>
              </div>
            </div>

            <div className="add-asset-dialog__actions">
              <Dialog.Close asChild>
                <button type="button" className="add-asset-dialog__cancel">
                  {t.cancel}
                </button>
              </Dialog.Close>
              <button
                type="submit"
                className="add-asset-dialog__submit"
                disabled={!isValid || isSubmitting}
              >
                <Plus size={16} />
                {isSubmitting ? t.submitting : t.submit}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
